/**
 * Scale drop form – confirm/save logic
 * For Media Devoted | Created By Ciro Autiero
 */

import type { ShortlinkClickIdStructure, ShortlinkDomainScheme } from '@trafficpulse/shared';

import {
  filterSenderNamesMax11,
  uniqueKeepOrderStrings,
} from '@/features/smswiser-campaigns/components/PrepareNewDropsForm';
import { getDefaultTldValue } from '@/features/smswiser-campaigns/utils/tldDefaults';
import type { DropsWithStatsRow } from '@/features/smswiser-campaigns/types/workspace.types';
import type { ShortLinkTld } from '@/hooks';

function parseExecuteAfter(executeAfterTrimmed: string): string | null | undefined {
  if (!executeAfterTrimmed) return undefined;
  if (executeAfterTrimmed.includes('Z') || executeAfterTrimmed.includes('+')) return executeAfterTrimmed;
  const d = new Date(executeAfterTrimmed);
  return !Number.isNaN(d.getTime()) ? d.toISOString() : undefined;
}

export async function executeScaleDropConfirm(params: {
  amount: number | '';
  routeId: number;
  tldValue: string;
  executeAfter: string;
  selectedSenderNames: string[];
  selectedSmsTexts: string[];
  perDropSmsTexts: string[][] | null;
  perDropSenderNames: string[][] | null;
  leadsListWiserId: number;
  isMulti: boolean;
  mode: 'scale' | 'edit' | 'create-from-lists';
  editDropId?: string;
  onCreateFromLists?: (payload: unknown, rows: DropsWithStatsRow[]) => void | Promise<void>;
  defaultWorkDate: string;
  titlePrefix: string;
  routeMatch: { id: number } | null;
  tldMatch: { value: string } | null;
  tlds: ShortLinkTld[];
  rows: DropsWithStatsRow[];
  onSave?: (p: {
    routeId: number;
    leadsListWiserId: number;
    shortLinkTldValue: string;
    amount: number;
    executeAfter?: string | null;
    senderNames: string[];
    smsTexts: string[];
    forceSend?: boolean;
    batchAmount?: number | null;
    batchDelay?: number | null;
    useOnlyNewDomains?: boolean;
    clickIdStructure?: ShortlinkClickIdStructure;
    domainScheme?: ShortlinkDomainScheme;
    staticTagId?: string | null;
  }) => void;
  onConfirm?: (payload: unknown, multiRows?: DropsWithStatsRow[]) => void;
  onClose: () => void;
  forceSend?: boolean;
  batchAmount?: number;
  batchDelay?: number;
  useOnlyNewDomains?: boolean;
  clickIdStructure?: ShortlinkClickIdStructure;
  domainScheme?: ShortlinkDomainScheme;
  scheduleIntervalMinutes?: number;
  selectedStaticTagId?: string | null | 'unchanged';
}): Promise<void> {
  const { amount, routeId, tldValue, executeAfter, selectedSenderNames, selectedSmsTexts, perDropSmsTexts, perDropSenderNames, leadsListWiserId, isMulti, mode, editDropId, defaultWorkDate, titlePrefix, routeMatch, tldMatch, tlds, rows, onSave, onConfirm, onCreateFromLists, onClose, selectedStaticTagId } = params;

  const resolvedStaticTagId = selectedStaticTagId === 'unchanged' ? null : (selectedStaticTagId ?? null);

  if (amount === '' || amount < 1) return;
  if (!isMulti) {
    if (!routeId || !tldValue) return;
    if (selectedSenderNames.length === 0 || selectedSmsTexts.length === 0) return;
    if (!leadsListWiserId) return;
  }

  if (mode === 'edit' && onSave && editDropId) {
    const executeAfterIso = parseExecuteAfter(executeAfter.trim()) ?? null;
    onSave({
      routeId,
      leadsListWiserId: leadsListWiserId ?? 0,
      shortLinkTldValue: tldValue,
      amount: amount as number,
      executeAfter: executeAfterIso,
      senderNames: filterSenderNamesMax11(uniqueKeepOrderStrings(selectedSenderNames)).slice(0, 50),
      smsTexts: uniqueKeepOrderStrings(selectedSmsTexts).slice(0, 50),
      forceSend: params.forceSend,
      batchAmount: params.batchAmount ?? null,
      batchDelay: params.batchDelay ?? null,
      useOnlyNewDomains: params.useOnlyNewDomains,
      clickIdStructure: params.clickIdStructure,
      domainScheme: params.domainScheme,
      staticTagId: resolvedStaticTagId,
    });
    onClose();
    return;
  }

  const handler = mode === 'create-from-lists' ? onCreateFromLists : onConfirm;
  if (!handler) return;

  try {
    const executeAfterIso = parseExecuteAfter(executeAfter.trim());
    const payload = {
      workDate: defaultWorkDate,
      titlePrefix,
      routeId: routeId > 0 ? routeId : (routeMatch?.id ?? 0),
      leadsListWiserId: leadsListWiserId ?? 0,
      shortLinkTldValue: tldValue || tldMatch?.value || getDefaultTldValue(tlds),
      amount,
      executeAfter: executeAfterIso,
      senderNames: filterSenderNamesMax11(uniqueKeepOrderStrings(selectedSenderNames)).slice(0, 50),
      smsTexts: uniqueKeepOrderStrings(selectedSmsTexts).slice(0, 50),
      forceSend: params.forceSend,
      batchAmount: params.batchAmount,
      batchDelay: params.batchDelay,
      useOnlyNewDomains: params.useOnlyNewDomains,
      clickIdStructure: params.clickIdStructure,
      domainScheme: params.domainScheme,
      scheduleIntervalMinutes: params.scheduleIntervalMinutes,
      staticTagId: resolvedStaticTagId,
      ...(isMulti && {
        routeIdOverride: routeId > 0 ? routeId : null,
        shortLinkTldOverride: tldValue.trim() ? tldValue.trim() : null,
        senderNamesOverride: perDropSenderNames == null && selectedSenderNames.length > 0 ? filterSenderNamesMax11(uniqueKeepOrderStrings(selectedSenderNames)).slice(0, 50) : null,
        smsTextsOverride: perDropSmsTexts == null && selectedSmsTexts.length > 0 ? uniqueKeepOrderStrings(selectedSmsTexts).slice(0, 50) : null,
        senderNamesOverridePerDrop: perDropSenderNames ?? undefined,
        smsTextsOverridePerDrop: perDropSmsTexts ?? undefined,
      }),
    };
    await handler(payload, rows);
    onClose();
  } catch {
    // Error already handled by onConfirm; modal stays open for retry
  }
}
